import { images } from "@/assets";
import {
  Badge,
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui";
import { useIsMobile } from "@/hooks/use-mobile";
import { BUDGET_YEAR, cn, formatAmount, paymentStatus, POSTING_YEAR, ROLE_OWNER, toApiSort } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { getOwnerBomBase, routes } from "@/routes/utils";
import { useGetInvoiceReports, useGetPostedDocumentList } from "@/services";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CREATED_BY,
  DATE_RANGE,
  DUE_DAYS,
  END_DATE,
  INVOICE_STATUS,
  PAYMENT_STATUS,
  START_DATE,
  VENDOR_ID,
} from "./consts";
import { BadgeInfo } from "lucide-react";

const useReports = () => {
  const fromDashboard = useLocation().pathname?.includes("/dashboard");
  const base = getOwnerBomBase(useLocation().pathname);
  const [showLinkPopup, setShowLinkPopup] = useState<boolean>(false);
  const [clearingId, setClearingId] = useState<string>("");
  const [reversalId, setReversalId] = useState<string>("");
  const [currentTab, setCurrentTab] = useState<string>("invoice_number");
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sorting, setSorting] = useState([{ id: "createdAt", desc: true }]);
  const [indicatorStyle, setIndicatorStyle] = useState({});
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [filterData, setFilterData] = useState<any>(null);
  const isMobile = useIsMobile();
  const [selectedOption, setSelectedOption] = useState<string>(BUDGET_YEAR);

  const {
    communityData: communityDetail,
    currentFinanceYear,
    currentRole,
  } = useSelector((state: RootState) => state.currentData);

  const [invoiceOverviewCards, setInvoiceOverviewCards] = useState([
    {
      title: "Total Invoice",
      count: 0,
      amt: 0,
      icon: images.dollar,
      key: "total",
    },
    {
      title: "Parked Invoice",
      count: 0,
      amt: 0,
      icon: images.report_parked,
      key: { count: "draft", amt: "draftInvoiceAmount" },
    },
    {
      title: "Pending  Approval",
      count: 0,
      amt: 0,
      icon: images.report_rejected,
      key: "pendingForApproval",
    },
    {
      title: "Approved Invoice",
      count: 0,
      amt: 0,
      icon: images.report_approved,
      key: { count: "approved", amt: "approvedInvoiceAmount" },
    },
    {
      title: "Posted Invoice",
      count: 0,
      amt: 0,
      icon: images.report_rejected,
      key: { count: "postedInvoice", amt: "postedInvoiceAmount" },
    },
  ]);

  const [paymentCards, setPaymentCards] = useState([
    {
      title: "Total",
      count: 0,
      amt: 0,
      icon: <img src={images.dollar} />,
      key: "total",
      filter: {
        status: "total",
      }
    },
    {
      title: "",
      count: 0,
      amt: 0,
      icon: <span className="text-green-600 text-[14px]">Paid</span>,
      key: { count: "paidInvoice", amt: "paidInvoiceAmount" },
      filter: {
        status: "paid",
      }
    },
    {
      title: "Due In",
      count: 0,
      amt: 0,
      icon: (
        <div className="flex flex-col items-center justify-center">
          <span className="text-primary text-[12px]">0-9</span>
          <span className="text-[14px]">Days</span>
        </div>
      ),
      key: { count: "lessThanTen", amt: "lessThanTenInvoiceAmount" },
      filter: {
        status: "dueIn",
        value: [0,9],
        operator: "BETWEEN"
      }
    },
    {
      title: "Due In",
      count: 0,
      amt: 0,
      icon: (
        <div className="flex flex-col items-center justify-center">
          <span className="text-primary text-[12px]">10-19</span>
          <span className="text-[14px]">Days</span>
        </div>
      ),
      key: { count: "tenToTwenty", amt: "tenToTwentyInvoiceAmount" },
      filter: {
        status: "dueIn",
        value: [10,19],
        operator: "BETWEEN"
      }
    },
    {
      title: "Due In",
      count: 0,
      amt: 0,
      icon: (
        <div className="flex flex-col items-center justify-center">
          <span className="text-primary text-[12px]">20+</span>
          <span className="text-[14px]">Days</span>
        </div>
      ),
      key: { count: "moreThanTwenty", amt: "moreThanTwentyInvoiceAmount" },
      filter: {
        status: "dueIn",
        value: 20,
        operator: "GREATER_THAN"
      }
    },
  ]);

  const [statusCards, setStatusCards] = useState([
    {
      invoices: { title: "# Invoices", count: 0, key: "total" },
      vendors: { title: "# Vendors", count: 0, key: "total" },
      amount: { title: "Amount", count: 0, key: "total" },
    },
    {
      invoices: { title: "# Rev Invoices", count: 0, key: "reversedInvoice" },
      vendors: { title: "# Vendors", count: 0, key: "reversedInvoiceVendors" },
      amount: { title: "Amount", count: 0, key: "reversedInvoiceAmount" },
    },
    {
      invoices: { title: "# Paid Invoices", count: 0, key: "paidInvoice" },
      vendors: { title: "# Vendors", count: 0, key: "paidInvoiceVendors" },
      amount: { title: "Amount", count: 0, key: "paidInvoiceAmount" },
    },
    {
      invoices: { title: "# Unpaid Invoices", count: 0, key: "unPaidInvoice" },
      vendors: { title: "# Vendors", count: 0, key: "unPaidInvoiceVendors" },
      amount: { title: "Amount", count: 0, key: "unPaidInvoiceAmount" },
    },
  ]);

  const {
    data: reportData,
    isLoading: isReportLoading,
    refetch: refetchInvoiceReports,
  } = useGetInvoiceReports({
    data: {
      communityId: communityDetail?.communityId,
      vendorId: [],
      ...(selectedOption == POSTING_YEAR ? {[POSTING_YEAR]: currentFinanceYear} : {[BUDGET_YEAR]: currentFinanceYear}),
      ...((filterData as any)?.[DATE_RANGE]
        ? {
            [START_DATE]: (filterData as any)?.[DATE_RANGE][START_DATE],
            [END_DATE]: (filterData as any)?.[DATE_RANGE][END_DATE],
          }
        : {}),
      ...((filterData as any)?.[INVOICE_STATUS]
        ? {
            [INVOICE_STATUS]: (filterData as any)?.[INVOICE_STATUS],
          }
        : {}),
      ...((filterData as any)?.[CREATED_BY]
        ? {
            [CREATED_BY]: (filterData as any)?.[CREATED_BY],
          }
        : {}),
      ...((filterData as any)?.[PAYMENT_STATUS]
        ? {
            [PAYMENT_STATUS]: (filterData as any)?.[PAYMENT_STATUS],
          }
        : {}),
    },
  });

  // Invoice Overview
  useEffect(() => {
    if (reportData?.data) {
      let totalCount = 0,
        totalAmt = 0;
      const cards = invoiceOverviewCards?.map((obj: any) => {
        if (obj.key !== "total") {
          totalAmt +=
            obj.key == "pendingForApproval"
              ? reportData?.data?.["pendingAGMApprovalAmount"] +
                reportData?.data?.["pendingCAMApprovalAmount"] +
                reportData?.data?.["pendingBOARDApprovalAmount"] +
                reportData?.data?.["pendingBOMApprovalAmount"]
              : reportData?.data?.[obj?.key?.amt] || 0;
          totalCount +=
            obj.key == "pendingForApproval"
              ? reportData?.data?.["pendingAGMApproval"] +
                reportData?.data?.["pendingCAMApproval"] +
                reportData?.data?.["pendingBOARDApproval"] +
                reportData?.data?.["pendingBOMApproval"]
              : reportData?.data?.[obj?.key?.count] || 0;
          return {
            ...obj,
            count:
              obj.key == "pendingForApproval"
                ? reportData?.data?.["pendingAGMApproval"] +
                  reportData?.data?.["pendingCAMApproval"] +
                  reportData?.data?.["pendingBOARDApproval"] +
                  reportData?.data?.["pendingBOMApproval"]
                : reportData?.data?.[obj?.key?.count] || 0,
            amt:
              obj.key == "pendingForApproval"
                ? reportData?.data?.["pendingAGMApprovalAmount"] +
                  reportData?.data?.["pendingCAMApprovalAmount"] +
                  reportData?.data?.["pendingBOARDApprovalAmount"] +
                  reportData?.data?.["pendingBOMApprovalAmount"]
                : reportData?.data?.[obj?.key?.amt] || 0,
          };
        } else {
          return obj;
        }
      });

      const idx = cards.findIndex((obj: any) => obj.key === "total");
      cards[idx] = { ...cards[idx], count: totalCount, amt: totalAmt };
      setInvoiceOverviewCards(cards);
    }
  }, [reportData]);

  //payment timeline
  useEffect(() => {
    if (reportData?.data) {
      let totalCount = 0,
        totalAmt = 0;
      const cards = paymentCards?.map((obj: any) => {
        if (obj.key !== "total") {
          totalAmt += reportData?.data?.[obj?.key?.amt] || 0;
          totalCount += reportData?.data?.[obj?.key?.count] || 0;
          return {
            ...obj,
            count: reportData?.data?.[obj?.key?.count] || 0,
            amt: reportData?.data?.[obj?.key?.amt] || 0,
          };
        } else {
          return obj;
        }
      });

      const idx = cards.findIndex((obj: any) => obj.key === "total");
      cards[idx] = { ...cards[idx], count: totalCount, amt: totalAmt };
      setPaymentCards(cards);
    }
  }, [reportData]);

  // invoice status breakdown
  useEffect(() => {
    if (!reportData?.data) return;

    let totalInvoices = 0;
    let totalVendors = 0;
    let totalAmount = 0;

    const updated = statusCards.map((card) => {
      const newCard = { ...card };

      // loop over invoices/vendors/amount
      (["invoices", "vendors", "amount"] as const).forEach((field) => {
        const key = newCard[field].key;
        const value = reportData?.data?.[key] ?? 0;

        newCard[field] = {
          ...newCard[field],
          count: value,
        };

        // collect totals for the last "total" row
        if (key !== "total") {
          if (field === "invoices") totalInvoices += value;
          if (field === "vendors") totalVendors += value;
          if (field === "amount") totalAmount += value;
        }
      });

      return newCard;
    });

    // put totals into the first row (where key === 'total')
    updated[0].invoices.count = totalInvoices;
    updated[0].vendors.count = totalVendors;
    updated[0].amount.count = totalAmount;

    setStatusCards(updated);
  }, [reportData]);

  const breadcrumbDataDashboard = useMemo(
    () => [
      {
        link: `/${base}/${routes.dashboard}`,
        name: `Dashboard`,
      },
      {
        link: "",
        name: (
          <span className="text-primary">
            Invoice Report (Year - {currentFinanceYear})
          </span>
        ),
      },
    ],
    []
  );

  const breadcrumbDataFinance = useMemo(
    () => [
      {
        link: `/${routes.bom}/${routes.finance}`,
        name: `Activity Dashboard`,
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.invoice}`,
        name: "Invoice",
      },
      {
        link: "",
        name: (
          <span className="text-primary">
            Invoice Report (Year - {currentFinanceYear})
          </span>
        ),
      },
    ],
    []
  );

  const breadcrumbData = fromDashboard
    ? breadcrumbDataDashboard
    : breadcrumbDataFinance;

  const {
    data: postedDocumentList,
    isLoading: isPostedDocLoading,
    refetch: refetchPostedDoc,
    isRefetching: isPostedDocRefetching,
  } = useGetPostedDocumentList({
    data: {
      filters: [
        {
          field: selectedOption == BUDGET_YEAR ? "budgetYear" : "year",
          value: currentFinanceYear,
          operator: "EQUAL",
        },
        {
          field: "communityId",
          value: communityDetail?.communityId,
          operator: "EQUAL",
        },
        {
          field: "documentType",
          value: ["VI", "VX"],
          operator: "CONTAINS_ANY",
        },
        {
          field: "status",
          value: filterData?.[INVOICE_STATUS]
            ? filterData?.[INVOICE_STATUS]
            : "POSTED",
          operator: "CONTAINS",
        },
        ...((filterData as any)?.[VENDOR_ID]
          ? [
              {
                field: VENDOR_ID,
                value: (filterData as any)?.[VENDOR_ID],
                operator: "CONTAINS_ANY",
              },
            ]
          : []),
        ...((filterData as any)?.[DATE_RANGE]
          ? [
              {
                field: START_DATE,
                value: (filterData as any)?.[DATE_RANGE][START_DATE],
                operator: "EQUAL",
              },
              {
                field: END_DATE,
                value: (filterData as any)?.[DATE_RANGE][END_DATE],
                operator: "EQUAL",
              },
            ]
          : []),
        ...((filterData as any)?.[CREATED_BY]
          ? [
              {
                field: CREATED_BY,
                value: (filterData as any)?.[CREATED_BY],
                operator: "EQUAL",
              },
            ]
          : []),
        ...((filterData as any)?.[PAYMENT_STATUS]
          ? [
              {
                field: PAYMENT_STATUS,
                value: (filterData as any)?.[PAYMENT_STATUS],
                operator: "EQUAL",
              },
            ]
          : []),
        ...((filterData as any)?.[DUE_DAYS]
          ? (filterData as any)?.[DUE_DAYS]
          : []),
      ],
      pageNumber,
      pageSize,
      sort: toApiSort(sorting, { field: "createdAt", direction: "DESC" }),
    },
    queryParams: {
      enabled: !!communityDetail?.communityId,
    },
  });

  useEffect(() => {
    refetchPostedDoc();
  }, [pageNumber, pageSize, sorting]);

  const columns = [
    {
      accessorKey: "documentNumber",
      header: (
        <div>
          Posted <br /> Doc. ID
        </div>
      ),
      accessorFn: (row: any) => String(row.documentNumber || ""),
      size: 50,
    },
    {
      accessorKey: "parkDocumentHeaderId",
      header: (
        <div>
          Parked <br /> Doc. ID
        </div>
      ),
      accessorFn: (row: any) => String(row.parkDocumentHeaderId || ""),
      size: 50,
    },
    {
      accessorKey: "reversalPostDocumentHeaderId",
      header: (
        <div>
          Reversal <br /> Doc. ID
        </div>
      ),
      accessorFn: (row: any) => String(row.reversalPostDocumentHeaderId || ""),
      size: 50,
    },
    {
      accessorKey: "vendorName",
      header: <div>Vendor Name</div>,
      accessorFn: (row: any) => String(row?.vendorName || ""),
    },
    {
      accessorKey: "invoiceNumber",
      header: <div>Invoice No.</div>,
      accessorFn: (row: any) => String(row?.invoiceNumber || ""),
    },
    {
      accessorKey: "invoiceAmount",
      meta: { align: "right" },
      header: <div>Invoice Amt.</div>,
      accessorFn: (row: any) => formatAmount(row?.invoiceAmount || 0),
    },
    {
      accessorKey: "approvedAmount",
      meta: { align: "right" },
      header: <div>Approved Amt.</div>,
      accessorFn: (row: any) => formatAmount(row?.approvedAmount || 0),
    },
    ...(currentRole == ROLE_OWNER && fromDashboard
      ? []
      : [
          {
            id: "linked_document",
            header: "Linked Document",
            cell: (row: any) => {
              return (
                <Button
                  variant="link"
                  type="button"
                  onClick={(e: any) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowLinkPopup(true);
                    setClearingId(row?.row?.original?.clearingId);
                    setReversalId(
                      row?.row?.original?.reversalPostDocumentHeaderId
                    );
                  }}
                >
                  View
                </Button>
              );
            },
          },
        ]),
    {
      accessorKey: "submittedOn",
      header: <div>Posted On</div>,
      accessorFn: (row: any) =>
        moment(row?.submittedOn || "").format("MM/DD/YYYY"),
    },
    {
      accessorKey: "dueDate",
      header: <div>Due Date</div>,
      accessorFn: (row: any) =>
        row?.dueDate ? moment(row?.dueDate || "").format("MM/DD/YYYY") : "",
    },
    {
      accessorKey: "paymentState",
      header: (
        <div>
          Payment <br/> Status
        </div>
      ),
      cell: ({row}: any) => {
        const isClear = row.original?.paymentState == "PAID";
        return (
          <Badge
            variant="outline"
            className={cn(
              isClear
                ? paymentStatus?.paid?.color
                : paymentStatus?.unpaid?.color,
              "rounded-full px-4 py-2"
            )}
          >
            {isClear ? "Paid" : "Unpaid"}
          </Badge>
        );
      },
    },
    {
      accessorKey: "baselineDate",
      header: <div>Baseline Date</div>,
      accessorFn: (row: any) =>
        row?.baselineDate ? moment(row?.baselineDate || "").format("MM/DD/YYYY") : "",
    },
    {
      accessorKey: "paymentTerms",
      header: <div>Payment Terms</div>,
      accessorFn: (row: any) => String(row?.paymentTerms || ""),
    },
    {
      accessorKey: "paymentMethod",
      header: <div>Payment Method</div>,
      accessorFn: (row: any) => String(row?.paymentMethod || ""),
    },
    {
      accessorKey: "dueDays",
      header: <div>Aging</div>,
      accessorFn: (row: any) => row?.dueDays == 0 ? "Due Today" : row?.dueDays < 0 ? `Past ${Math.abs(row?.dueDays)} Days` : `${row?.dueDays} Days`,
    },
    {
      accessorKey: "headerItemText",
      header: "",
      cell: ({row}: any) => {
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger className="mx-1" type="button">
                <BadgeInfo className="h-5 w-5 stroke-yellow-600" />
              </TooltipTrigger>
              <TooltipContent>
                {row.original?.headerItemText || ""}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
    },
  ];

  const viewPaymentDoc = () => {
    if (clearingId) {
      navigate(
        `/${routes.bom}/${routes.finance}/${routes.payment}/all/${clearingId}/view`,
        {
          state: { from: "reports" },
        }
      );
    }
  };

  const viewReversalDoc = () => {
    if (reversalId) {
      navigate(
        `/${routes.bom}/${routes.finance}/${routes.invoice}/posted/${reversalId}/view`,
        {
          state: { from: "reports" },
        }
      );
    }
  };

  const onViewDetail = () => {
    setShowDetails((prev) => !prev);
  };

  const onTabChange = (tab: "invoice_number" | "invoice_amount") => {
    setCurrentTab(tab);
  };

  useEffect(() => {
    if (!containerRef.current) return;
    const activeEl = containerRef.current.querySelector(
      `[data-tab="${currentTab}"]`
    ) as HTMLElement;
    if (activeEl) {
      const { offsetLeft, offsetWidth } = activeEl;
      setIndicatorStyle({
        left: offsetLeft,
        width: offsetWidth,
      });
    }
  }, [currentTab]);

  const onCancelFilter = () => {
    setOpenFilter(false);
  };

  const onFilterBtnClick = () => {
    setOpenFilter(true);
  };

  const onBackClick = () => {
    navigate(
      fromDashboard
        ? `/${base}/${routes.dashboard}`
        : `/${routes.bom}/${routes.finance}/${routes.invoice}`
    );
  };

  useEffect(() => {
    refetchInvoiceReports();
  }, [filterData]);

  const onOptionChanges = (value: string) => {
    setSelectedOption(value);
    setTimeout(() => {
      refetchInvoiceReports();
      refetchPostedDoc()
    }, 10);
  };

  const onTimelineCardClick = (filter: any) => {
    const _filter = {...filterData};
    const { status, value, operator } = filter || {};

    if ( status == "paid") {
      delete _filter[DUE_DAYS];
      setFilterData({..._filter, [PAYMENT_STATUS]: "PAID"})
    } else if (status == "dueIn") {
      delete _filter[PAYMENT_STATUS];
      const data = [
        {
          field: DUE_DAYS,
          value,
          operator,
        },
      ]
      setFilterData({..._filter, [DUE_DAYS]: data})
    } else {
      delete _filter[PAYMENT_STATUS];
      delete _filter[DUE_DAYS];
      setFilterData({..._filter});
    }
  }

  return {
    breadcrumbData,
    onBackClick,
    communityDetail,
    invoiceOverviewCards,
    columns,
    viewPaymentDoc,
    viewReversalDoc,
    showLinkPopup,
    setShowLinkPopup,
    clearingId,
    reversalId,
    onViewDetail,
    showDetails,
    isReportLoading,
    paymentCards,
    statusCards,
    setPageNumber,
    setPageSize,
    postedDocumentList,
    isPostedDocLoading,
    isPostedDocRefetching,
    pageSize,
    sorting,
    setSorting,
    pageNumber,
    onTabChange,
    currentTab,
    containerRef,
    indicatorStyle,
    onCancelFilter,
    onFilterBtnClick,
    openFilter,
    filterData,
    setFilterData,
    isMobile,
    fromDashboard,
    onOptionChanges,
    selectedOption,
    onTimelineCardClick
  };
};

export default useReports;
